import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight, Building2, User, Upload, CircleDot, FileSignature, Briefcase } from "lucide-react";
import { ImportKlienta } from "../components/PrevodKlienta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import ClientForm from "../components/ClientForm";
import FiltrLista, { useFiltr, SkrytoFiltrem, PrazdnyFiltr } from "../components/FiltrLista";
import { Button, Spinner } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import { relDay } from "../lib/format";
import { odpovida, zmenaHodnoty } from "../lib/filtr";
import { poleTechnologie, polePartner, poleMesto, poleZmena, polePevne } from "../lib/filtryPole";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";

const TVARY = ["klient", "klienti", "klientů"];
const ANO_NE = [{ v: "ANO", label: "Má" }, { v: "NE", label: "Nemá" }];
// Bez kontaktu: poslední aktivita před víc než 90 dny, nebo žádná.
const bezKontaktu = (c) => !c.posledni_aktivita || zmenaHodnoty(c.posledni_aktivita)[0] === "STARSI";

export default function Clients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useUrlStav("q", "");
  const [pohled, setPohled] = useUrlStav("pohled", "vse");
  const [form, setForm] = useState(false);
  const [importOkno, setImport] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/api/clients", { params: { q, limit: 1000, filtry: true } });
    setItems(data.items);
    setTotal(data.total);
  }, [q]);

  const pole = useMemo(() => [
    polePevne({ klic: "typ", nazev: "Typ", rod: "m", ikona: User, tvary: ["typ", "typy", "typů"],
      hodnoty: [{ v: "OSOBNI", label: "Osoba" }, { v: "FIRMA", label: "Firma" }], hodnotyPolozky: (c) => [c.typ || "OSOBNI"] }),
    polePevne({ klic: "stav", nazev: "Stav", rod: "m", ikona: CircleDot, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "AKTIVNI", label: "Aktivní" }, { v: "NEAKTIVNI", label: "Neaktivní" }],
      hodnotyPolozky: (c) => [c.status === "NEAKTIVNI" ? "NEAKTIVNI" : "AKTIVNI"] }),
    polePevne({ klic: "smlouva", nazev: "Servisní smlouva", rod: "z", ikona: FileSignature, tvary: ["možnost", "možnosti", "možností"],
      hodnoty: ANO_NE, hodnotyPolozky: (c) => [c.servisni_smlouva ? "ANO" : "NE"] }),
    poleTechnologie((c) => c.technologie || []),
    polePevne({ klic: "pripad", nazev: "Otevřený případ", rod: "m", ikona: Briefcase, tvary: ["možnost", "možnosti", "možností"],
      hodnoty: ANO_NE, hodnotyPolozky: (c) => [c.otevreny_pripad ? "ANO" : "NE"] }),
    ...((items || []).some((c) => "partner" in c) ? [polePartner((c) => c.partner)] : []),
    poleMesto((c) => c.adresa?.mesto),
    { ...poleZmena((c) => c.posledni_aktivita, { klic: "aktivita", nazev: "Poslední aktivita" }),
      hodnoty: () => [{ v: "D30", label: "Posledních 30 dní" }, { v: "D90", label: "Posledních 90 dní" },
        { v: "STARSI", label: "Starší než 90 dní" }, { v: "NIKDY", label: "Žádná" }],
      hodnotyPolozky: (c) => (c.posledni_aktivita ? zmenaHodnoty(c.posledni_aktivita) : ["NIKDY"]) },
  ], [items]);
  const [filtry, setFiltry] = useFiltr(pole);
  const vsechny = useMemo(() => items || [], [items]);
  const pohledy = useMemo(() => [
    { klic: "vse", nazev: "Všichni", test: () => true },
    { klic: "moje", nazev: "Moje", test: (c) => (c.tym_ids || []).includes(user?.id) || (c.obchodnik_ids || []).includes(user?.id) },
    { klic: "bez_kontaktu", nazev: "Bez kontaktu 90+ dní", test: bezKontaktu },
  ].map((p) => ({ ...p, pocet: vsechny.filter(p.test).length })), [vsechny, user?.id]);
  const rozsah = useMemo(() => vsechny.filter((pohledy.find((p) => p.klic === pohled) || pohledy[0]).test), [vsechny, pohledy, pohled]);
  const shown = useMemo(() => rozsah.filter((c) => odpovida(c, filtry, pole)), [rozsah, filtry, pole]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Obchod / Klienti" title="Klienti"
        subtitle="Přehled klientů firmy."
        actions={
          canWrite && (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setImport(true)} data-testid="import-client" title="Klient ze souboru .ppklient od jiné firmy">
                <Upload size={16} strokeWidth={2} /> <span className="hidden sm:inline">Import</span>
              </Button>
              <Button onClick={() => setForm(true)} data-testid="new-client">
                <Plus size={16} strokeWidth={2} /> Nový klient
              </Button>
            </div>
          )
        } />

      <FiltrLista pole={pole} polozky={rozsah} filtry={filtry} setFiltry={setFiltry} tvary={TVARY}
        pohledy={pohledy} pohled={pohled} setPohled={setPohled} vysledek={shown.length} celkem={rozsah.length}
        hledani={{ q, setQ, placeholder: "Hledat jméno, e-mail, město…", testid: "clients-search" }} testid="clients-filtr" />
      {items && total > items.length && (
        <div className="mb-3 t-meta text-muted" data-testid="clients-orez">Zobrazeno {items.length} z {total} — upřesni hledání.</div>
      )}

      <div className="card overflow-hidden">
        {items === null ? (
          <div className="p-10 flex justify-center text-muted"><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyState icon="Users" title={q ? "Nic nenalezeno" : "Zatím žádní klienti"}
            description={q ? "Zkus jiné hledání." : "Založ prvního klienta a začni evidovat aktivity."}
            action={canWrite && !q && (
              <Button onClick={() => setForm(true)} data-testid="new-client-empty">
                <Plus size={16} /> Nový klient
              </Button>
            )} />
        ) : shown.length === 0 ? (
          filtry.length ? <PrazdnyFiltr nazev="Žádný klient" onVymazat={() => setFiltry([])} />
            : <EmptyState icon="Users" title="V tomhle pohledu nikdo není" description="Přepni na Všichni." />
        ) : (
          <>
            <div className="hidden md:grid grid-cols-[1fr_140px_150px_1fr_120px_24px] gap-3 px-4 py-2.5 label-up text-muted border-b border-line">
              <span>Klient</span><span>Město</span><span>Telefon</span><span>E-mail</span>
              <span>Poslední aktivita</span><span />
            </div>
            {shown.map((c) => (
              <button key={c.id} onClick={() => nav(`/klienti/${c.id}`)} data-testid={`client-row-${c.id}`}
                className="w-full text-left grid md:grid-cols-[1fr_140px_150px_1fr_120px_24px] grid-cols-[1fr_24px] gap-3 items-center px-4 py-3.5 border-b border-line-softer last:border-0 hover:bg-inputbg transition-colors">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="h-8 w-8 rounded-icon bg-inputbg border border-line-strong flex items-center justify-center shrink-0">
                    {c.typ === "FIRMA" ? <Building2 size={15} className="text-sec" /> : <User size={15} className="text-sec" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-700 text-ink truncate">{c.display_name}</span>
                    <span className="md:hidden block text-[11.5px] text-muted truncate">{c.adresa?.mesto} · {c.telefon}</span>
                  </span>
                </span>
                <span className="hidden md:block text-[12.5px] text-sec truncate">{c.adresa?.mesto || "—"}</span>
                <span className="hidden md:block text-[12.5px] text-sec amount truncate">{c.telefon || "—"}</span>
                <span className="hidden md:block text-[12.5px] text-sec truncate">{c.email || "—"}</span>
                <span className="hidden md:block text-[12px] text-muted">{c.posledni_aktivita ? relDay(c.posledni_aktivita) : "—"}</span>
                <ChevronRight size={16} className="text-muted justify-self-end" />
              </button>
            ))}
            <SkrytoFiltrem skryto={filtry.length ? rozsah.length - shown.length : 0} tvary={TVARY} onZobrazit={() => setFiltry([])} />
          </>
        )}
      </div>

      {form && <ClientForm onClose={() => setForm(false)} onSaved={load} />}
      {importOkno && <ImportKlienta onClose={() => setImport(false)} onDone={(id) => nav(`/klienti/${id}`)} />}
    </div>
  );
}
